import { Controller, useFormContext } from 'react-hook-form';

import { DebounceSelect, ValueType } from 'components/DebounceSelect';
import { Label } from 'components/Label';
import { Flex } from 'components/layout/Flex';
import { Typography } from 'components/typography/Typography';
import { EmailTemplate } from 'types/EmailTemplates';
import { mapEmailTemplatesToSelectOptions } from 'utils/mapEmailTemplatesToSelectOptions';

type EmailTemplateSelectProps = {
  emailTemplates: EmailTemplate[];
  onTemplateSelect: (selectedTemplate: ValueType) => void;
  fieldName?: string;
  label?: string;
  placeholder?: string;
  className?: string;
};

const EmailTemplateSelect = ({
  emailTemplates,
  onTemplateSelect,
  fieldName = 'emailTemplateId',
  label = 'Email Template',
  placeholder = 'Select an email template',
  className = '',
}: EmailTemplateSelectProps) => {
  const { control } = useFormContext();

  return (
    <Flex direction='col' gap='xs' className={`w-full ${className}`}>
      <Label name={fieldName}>{label}</Label>
      <Controller
        name={fieldName}
        control={control}
        render={({ field }) => (
          <DebounceSelect
            showSearch
            className='w-full max-w-full h-10 text-sm'
            placeholder={placeholder}
            optionFilterProp='children'
            fetchOptions={() => Promise.resolve([])}
            onChange={(value) => {
              if (!value) {
                field.onChange(null);
                return;
              }
              const valueToUse =
                typeof value === 'object' && 'value' in value
                  ? value.value
                  : value;
              field.onChange(valueToUse);
              onTemplateSelect(value);
            }}
            value={field.value}
            options={mapEmailTemplatesToSelectOptions(emailTemplates)}
            optionRender={(option) => {
              // The template data is spread directly into the option object
              // not nested under option.data
              const emailTemplate = option as unknown as EmailTemplate;

              // Safety check: ensure emailTemplate and name exist
              if (!emailTemplate || !emailTemplate.name) {
                return (
                  <Typography variant='body-sm'>Unnamed Template</Typography>
                );
              }

              return (
                <Flex direction='col'>
                  <Typography variant='body-sm' weight='semibold'>
                    {emailTemplate.name}
                  </Typography>
                  {emailTemplate.subject && (
                    <Typography variant='body-xs' className='text-neutral-500'>
                      {emailTemplate.subject}
                    </Typography>
                  )}
                </Flex>
              );
            }}
            notFoundContent={
              <Typography variant='body-sm' className='text-neutral-500'>
                No templates found
              </Typography>
            }
          />
        )}
      />
    </Flex>
  );
};

export default EmailTemplateSelect;
